import React from "react";
import log from "loglevel";
import { Action } from "typescript-fsa";
import { FormattedMessage } from "react-intl";
import { Map } from "immutable";
import { Button } from "reactstrap";

import { WaIcon } from "@ui/WaIcon";
import { Spinner } from "@ui/Spinner";
import { ApiRequest } from "../../requests";

interface DownloadButtonProps extends React.Props<DownloadButton> {
    url?: string;
    download?: () => Action<ApiRequest>;
    disabled?: boolean;
    className?: string;
    buttonText?: string;
    requests?: Map<string, ApiRequest>;
    style?: "button" | "icon" | "text";
    actionCreator?: () => Action<ApiRequest>;
    children?: React.ReactNode;
}

interface DownloadButtonState {
    currentRequest: ApiRequest;
    loading: boolean;
}

class DownloadButton extends React.Component<DownloadButtonProps, DownloadButtonState> {
    constructor(props: DownloadButtonProps) {
        super(props);

        // Initialise the state.
        this.state = {
            currentRequest: null,
            loading: false,
        };
    }

    public componentWillReceiveProps(nextProps: DownloadButtonProps) {
        // Check if the request has updated
        const { currentRequest } = this.state;
        if (currentRequest != null) {
            const nextRequest = nextProps.requests.get(currentRequest.uuid);
            // If the request exists and has changed state.
            if (nextRequest != null && currentRequest.state !== nextRequest.state) {
                const nextState: DownloadButtonState = {
                    currentRequest: nextRequest,
                    loading: false,
                };
                // Set loading if in progress.
                if (nextRequest.isInProgress()) {
                    nextState.loading = true;
                }
                this.setState(nextState);
            }
        }
    }

    private handleClick(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        const { download } = this.props;
        if (download != null) {
            if (!this.state.loading) {
                const action = download();
                this.setState({
                    currentRequest: action.payload,
                    loading: true,
                });
            }
        }
        else {
            log.error("Cannot perform a download without a supplied 'download action'");
        }
    }

    public render(): JSX.Element {
        let { disabled } = this.props;
        const { style, children } = this.props;
        const { loading } = this.state;
        let Tag = style === "text" ? "a" : Button;
        // Set the class.
        let className = "";
        if (this.props.className != null) {
            className += this.props.className;
        }
        if (loading) {
            if (Tag === "a") {
                Tag = "span";
            }
            disabled = true;
        }

        // And the text.
        let buttonText: React.ReactNode = (
            <FormattedMessage id="ui.button.download"
                defaultMessage="Download" />
        );
        if (loading) {
            buttonText = (
                <span>
                    <Spinner inline color={style === "text" ? "dark" : "light"} />
                    <FormattedMessage id="ui.async.loading"
                        defaultMessage="Loading" />
                </span>
            );
        }
        else if (this.props.buttonText != null) {
            buttonText = (
                <span>{this.props.buttonText}</span>
            );
        }
        else if (children != null) {
            buttonText = (children);
        }

        if (style === "icon" && loading) {
            return <span className={className}><Spinner inline /></span>;
        }
        else if (style === "icon" && !loading) {
            const aProps: any = {
                href: "#",
                onClick: this.handleClick.bind(this),
                className,
            };
            if (this.props.buttonText) {
                aProps.title = this.props.buttonText;
            }
            return (
                <a {...aProps}>
                    <WaIcon name="download" />
                </a>
            );
        }
        return (
            <Tag
                outline={Tag === Button ? true : undefined}
                tag={Tag === Button ? "a" : undefined}
                href={Tag === "a" ? "#" : undefined}
                disabled={disabled}
                className={className}
                onClick={this.handleClick.bind(this)}
            >{buttonText}</Tag>
        );
    }
}

export type { DownloadButtonProps };
export {
    DownloadButtonState,
    DownloadButton,
};
